import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { motion, useScroll, useTransform } from "framer-motion";
import { testimonialService } from '../../services/testimonialService';
import main1 from "../../static/images/landing_page/main_1.png";
import main2 from "../../static/images/landing_page/main_2.png";
import main3 from "../../static/images/landing_page/main_3.png";
import conditionSurvey from "../../static/images/services/condition_survery_onhire.png";
import dryBoxService from "../../static/images/services/dry_box_service.png";
import eiReceipt from "../../static/images/services/ei_receipt.jpg";
import tankInspection from "../../static/images/services/tank_inspection_report.png";
import tankCleaning from "../../static/images/services/tc.jpg";

/* =======================
   Animation Variants
======================= */
const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0 },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    contact_number: '',
    email: '',
    location: '',
    rating: 5,
    testimonial_text: '',
    image: null
  });
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleServiceCardClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const carouselImages = [
  {
    url: main1,
    title: 'Professional Marine Consultancy',
    subtitle: 'Trusted expertise in shipping and maritime services'
  },
  {
    url: main2,
    title: 'Comprehensive Survey Services',
    subtitle: 'IMO tank container certification and condition surveys'
  },
  {
    url: main3,
    title: 'Global Recognition',
    subtitle: 'Serving clients in India and overseas with excellence'
  }
];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);

  // Fetch testimonials from API
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      // Pass paginated=false to get all testimonials without pagination
      const response = await testimonialService.getAllTestimonials(1, 100, true, true);
      console.log('API Response:', response); // Debug log
      
      // Extract testimonial data from response
      const testimonialData = response.data?.data || [];
      
      console.log('Testimonial Data:', testimonialData); // Debug log
      
      // Sort by rating (descending) then by created_at (descending)
      const sorted = testimonialData.sort((a, b) => {
        if (b.rating !== a.rating) {
          return b.rating - a.rating;
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setTestimonials(sorted);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      console.error('Error details:', error.response); // More detailed error
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, image: e.target.files[0] }));
    }
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('contact_number', formData.contact_number);
      submitData.append('email', formData.email);
      submitData.append('location', formData.location);
      submitData.append('rating', formData.rating);
      submitData.append('testimonial_text', formData.testimonial_text);
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      await testimonialService.createTestimonial(submitData);
      
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setSubmitSuccess(false);
        setFormData({
          full_name: '',
          contact_number: '',
          email: '',
          location: '',
          rating: 5,
          testimonial_text: '',
          image: null
        });
        fetchTestimonials();
      }, 2000);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Failed to submit testimonial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <svg key={i} className={`w-5 h-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const getImageSrc = (testimonial) => {
    if (testimonial.image_base64) {
      return `data:image/jpeg;base64,${testimonial.image_base64}`;
    }
    return null;
  };

  const renderProfileImage = (testimonial) => {
    const imageSrc = getImageSrc(testimonial);
    
    if (imageSrc) {
      return (
        <img 
          src={imageSrc} 
          alt={testimonial.full_name}
          className="w-16 h-16 rounded-full object-cover border-2 border-orange shadow-md"
        />
      );
    }
    
    // Default profile icon when no image
    return (
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange to-orange-600 flex items-center justify-center border-2 border-white shadow-md">
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      </div>
    );
  };

  const { scrollY } = useScroll();
  // Parallax movement (smooth & subtle)
  const imageY = useTransform(scrollY, [0, 600], [0, 140]);
  const contentY = useTransform(scrollY, [0, 600], [0, -60]);


  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Carousel Section with Parallax */}
      <section id="home" className="relative h-[90vh] mt-20 overflow-hidden">
        <div className="relative h-full overflow-hidden">
          {carouselImages.map((slide, index) => (
            <motion.div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              {/* Parallax Image */}
              <motion.img
                src={slide.url}
                alt={slide.title}
                style={{ y: imageY }}
                className="w-full h-full object-cover scale-110"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent">
                <div className="container mx-auto px-4 h-full flex items-center">
                  {/* Parallax Content */}
                  <motion.div
                    style={{ y: contentY }}
                    className="max-w-2xl text-white"
                  >
                    <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                      {slide.title}
                    </h2>
                    <p className="text-xl md:text-2xl mb-8 text-gray-200">
                      {slide.subtitle}
                    </p>

                    <div className="flex gap-4 flex-wrap">
                      <a
                        href="#contact"
                        className="px-8 py-4 bg-orange hover:bg-orange text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      >
                        Get Started
                      </a>
                      <a
                        href="#services"
                        className="px-8 py-4 border-2 border-orange text-orange font-bold rounded-lg hover:bg-orange hover:text-white hover:border-white transition-all"
                      >
                        Our Services
                      </a>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Left Arrow */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 backdrop-blur-sm text-white p-3 rounded-full transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 backdrop-blur-sm text-white p-3 rounded-full transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? "bg-white w-8" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>


      {/* About Section */}
      <motion.section
        id="about"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h3 className="text-4xl font-semibold text-orange mb-6">About United Marine Agency LLP</h3>
              <p className="text-lg text-gray-500 leading-relaxed">
                Incorporated in 2025, United Marine Agency LLP is a premier marine consultancy and survey company based in Mumbai, India. Our team of professionals brings vast experience in shipping, surveying, and marine consultancy. We are recognized both in India and overseas for our diligence, personal reputation, and professional expertise.
              </p>
            </div>

            <section className="py-0 bg-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {[
                    {
                      title: "Our Vision",
                      text: "To be the most trusted name in maritime consulting, representing clients in high-profile shipping incidents globally.",
                      icon: (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M12 3c4.97 0 9 3.582 9 8 0 5.25-7.5 10-9 10s-9-4.75-9-10c0-4.418 4.03-8 9-8z"
                        />
                      ),
                    },
                    {
                      title: "Our Strategy",
                      text: "Providing factual and flawless reporting through the expertise of our dedicated marine consultants and surveyors.",
                      icon: (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      ),
                    },
                    {
                      title: "Our Team",
                      text: "A team of seasoned professionals recognized for diligence, experience, and professional excellence.",
                      icon: (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M12 14c3.866 0 7 3.134 7 7M5 21c0-3.866 3.134-7 7-7m0-4a4 4 0 100-8 4 4 0 000 8z"
                        />
                      ),
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="group relative bg-white rounded-sm border border-gray-200
                      p-6 shadow-sm hover:shadow-xl transition-all duration-300"
                    >
                      {/* Top Accent Line */}
                      <span className="absolute top-0 left-0 h-1 w-0 bg-orange transition-all duration-300 group-hover:w-full rounded-t-2xl"></span>

                      {/* Header: Icon + Title */}
                      <div className="flex items-center gap-4 mb-5">
                        <div
                          className="w-10 h-10 rounded-sm bg-orange/10 flex items-center justify-center shrink-0
                          transition-transform duration-300 group-hover:scale-105"
                        >
                          <svg
                            className="w-7 h-7 text-orange"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {item.icon}
                          </svg>
                        </div>

                        <h4 className="text-2xl font-semibold text-gray-900 leading-tight">
                          {item.title}
                        </h4>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
      </motion.section>

      {/* Services Section - UPDATED with clickable cards and images */}
      <motion.section
        id="services"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 pt-14 bg-gray-50"
      >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-semibold text-orange mb-5">Our Services</h3>
              <p className="text-lg text-gray-500 leading-relaxed">Comprehensive marine consultancy and survey solutions</p>
            </div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              transition={{ staggerChildren: 0.15 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
              >
                {[
                  {
                    title: "Condition Survey Import/Export",
                    desc: "Detailed condition surveys during import and export operations from Mumbai Port and JNPT.",
                    image: conditionSurvey,
                    path: "/services/condition-survey"
                  },
                  {
                    title: "IMO Tank Container Certification",
                    desc: "Comprehensive checking and issuing of cleanliness certificates for all IMO tank containers.",
                    image: tankCleaning,
                    path: "/services/imo-tank-certification"
                  },
                  {
                    title: "On/Off Hire Survey",
                    desc: "Professional on-hire and off-hire surveys of marine containers with detailed documentation.",
                    image: dryBoxService,
                    path: "/services/on-off-hire-survey"
                  },
                  {
                    title: "Pre/Post Repair Survey",
                    desc: "Comprehensive surveys for dry van, reefer, and tank containers before and after repairs.",
                    image: eiReceipt,
                    path: "/services/pre-post-repair-survey"
                  },
                  {
                    title: "Tank Inspection Report",
                    desc: "Professional inspection and certification reports for ISO tank containers ensuring safety and compliance.",
                    image: tankInspection,
                    path: "/services/tank-inspection-report"
                  }
                ].map((service, idx) => (
                <button
                  key={idx}
                  onClick={() => handleServiceCardClick(service.path)}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-200 overflow-hidden group cursor-pointer"
                >
                  <motion.div
                    variants={fadeUp}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full"
                  >
                    {/* Image container */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h4 className="text-xl font-semibold mb-2 text-gray-700 text-left">
                        {service.title}
                      </h4>
                      <p className="text-gray-600 text-md leading-relaxed text-left">
                        {service.desc}
                      </p>
                    </div>
                  </motion.div>
                </button>
              ))}
            </motion.div>
          </div>
      </motion.section>

      {/* Clients Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 pt-14 bg-white"
      >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-semibold text-orange mb-5">Our Trusted Clients</h3>
              <p className="text-lg text-gray-500 leading-relaxed">Serving leading logistics and shipping companies</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {[
                'Fairfreight Lines Pvt. Ltd.',
                'Taewoong Logistics',
                'Chemnest Logistics',
                'Pristine Logistics',
                'MAK Container Line',
                'Glitter Shipping Services',
                'Elephant Line',
                'Samudera Naga Global',
                'Navin Florin International',
                'Industrial Solvents & Chemicals',
                'Dorf Ketal',
                'Deccan Transcon'
              ].map((client, idx) => (
                <motion.div
                key={idx}
                variants={fadeUp}
                transition={{ duration: 0.4 }}
              >
                <div
                  key={idx}
                  className="bg-gray p-6 rounded-sm flex items-center justify-center text-center border border-gray-200 transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-lg hover:bg-gray-50">
                  <p className="text-lg font-medium text-gray-600">{client}</p>
                </div>
              </motion.div>
              ))}
            </div>
          </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 pt-14 bg-gray-50"
      >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-semibold text-orange mb-5">What Our Clients Say</h3>
              <p className="text-lg text-gray-500 leading-relaxed">Trusted by businesses worldwide</p>
              
              {/* Add Testimonial Button */}
              <button
                onClick={() => setShowModal(true)}
                className="mt-6 px-6 py-3 bg-orange text-white font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
              >
                Share Your Experience
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.length > 0 ? (
                testimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id || idx}
                    variants={fadeUp}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    className="bg-white p-6 rounded-2xl border shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Profile Image */}
                    <div className="flex justify-center mb-4">
                      {renderProfileImage(testimonial)}
                    </div>
                    
                    <p className="text-gray-700 text-md leading-relaxed mb-4">
                      "{testimonial.testimonial_text}"
                    </p>
                    <div className="flex gap-1 mb-3">
                      {renderStars(testimonial.rating)}
                    </div>
                    <h4 className="font-semibold text-gray-600 text-lg">{testimonial.full_name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{testimonial.location}</p>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No testimonials yet. Be the first to share your experience!</p>
                </div>
              )}
            </div>
          </div>
      </motion.section>

      {/* Testimonial Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">Share Your Experience</h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSubmitSuccess(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {submitSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">Thank You!</h4>
                <p className="text-gray-600">Your testimonial has been submitted successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitTestimonial} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                      <input
                        type="tel"
                        name="contact_number"
                        value={formData.contact_number}
                        onChange={handleInputChange}
                        maxLength="10"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent"
                        placeholder="9876543210"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent"
                      placeholder="City, Country"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                          className="focus:outline-none"
                        >
                          <svg 
                            className={`w-8 h-8 ${star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                      <span className="ml-2 text-gray-600 self-center">{formData.rating} Star{formData.rating !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Testimonial *</label>
                    <textarea
                      name="testimonial_text"
                      value={formData.testimonial_text}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent resize-none"
                      placeholder="Share your experience with us..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Photo (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange hover:file:bg-orange-100"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setSubmitSuccess(false);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-orange text-white font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Submitting...' : 'Submit Testimonial'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      {/* Contact Section */}
      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-16 pt-14 bg-white text-center"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl font-semibold text-orange mb-5">Get In Touch</h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Ready to experience professional marine consultancy services? Contact us today!
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12 mt-10">
              <div className="bg-gray-50 p-6 rounded-sm border">
                <svg className="w-8 h-8 mx-auto mb-4" fill="orange" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <h4 className="font-semibold mb-2">Phone</h4>
                <p>+91 98210 15469</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-sm border">
                <svg className="w-8 h-8 mx-auto mb-4" fill="orange" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h4 className="font-semibold mb-2">Email</h4>
                <p>porusd@unitedllp.com</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-sm border">
                <svg className="w-8 h-8 mx-auto mb-4" fill="orange" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h4 className="font-semibold mb-2">Location</h4>
                <p>Mumbai, India</p>
              </div>
            </div>
            
            <a
              href="mailto:porusd@unitedllp.com"
              className="inline-block px-8 py-4 bg-orange text-white font-semibold rounded-md hover:bg-orange transition-all shadow-sm hover:shadow-3xl transform hover:-translate-y-1 text-lg"
            >
              Send Us a Message
            </a>
          </div>
        </div>
      </motion.section>
      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  );
};

export default LandingPage;